import { parseRoundFeed, parseCandleFeed, type RoundFeed, type CandleFeed } from './model';

/** Rounds feed plus `serverDate`: the response `Date` header in epoch seconds, if present and valid. */
export type RoundFetch = RoundFeed & { serverDate?: number };
export interface ShortMarketSource {
  rounds(signal: AbortSignal): Promise<RoundFetch>;
  candles(roundId: string, signal: AbortSignal): Promise<CandleFeed>;
}
export const createShortMarketSource = (base: string): ShortMarketSource => {
  // Static JSON files served same-origin (nginx); no browser calls to an unconfirmed price provider.
  if (!base.startsWith('/') || base.startsWith('//') || /[\\?#\s]/.test(base))
    throw new Error('Short-market API must be a same-origin path');
  const root = base.replace(/\/$/, '');
  const get = async (path: string, signal: AbortSignal) => {
    const response = await fetch(`${root}${path}`, {
      signal,
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    // Not shown to users: the page renders its own translated notice on any failure.
    // `status` lets the poll tell a missing file (404) from other errors.
    if (!response.ok)
      throw Object.assign(new Error(`Short-market feed unavailable (HTTP ${response.status})`), {
        status: response.status,
      });
    return { body: await response.json(), date: response.headers?.get('Date') };
  };
  return {
    rounds: async signal => {
      const { body, date } = await get('/rounds.json', signal);
      const serverDate = date ? Date.parse(date) / 1000 : NaN;
      return Number.isFinite(serverDate) ? { ...parseRoundFeed(body), serverDate } : parseRoundFeed(body);
    },
    candles: async (id, signal) => parseCandleFeed((await get(`/candles/${encodeURIComponent(id)}.json`, signal)).body, id),
  };
};
